import * as React from 'react';
import { Field, FieldArray, FormikProps, FormikHelpers as FormikActions, FieldArrayRenderProps } from 'formik';

import { GeneralInformationFormData, WeddingPacketFileInfo, VideoData } from './types';
import TextArea from '../input-textarea';

import TextInput from '../input-text';
import MaterialSelectInput from '../input-select';

import InputFile from '../input-file';

import filesize from 'filesize';
import { SpinnerOverlay } from '@weddingspot/ws-core-components';
import { WsFormikError } from '../../utils/formik/error';

interface VideosListItem {
    url: string;
    order: number;
}

interface FilesListItem {
    file_id: number;
    url: string;
    name: string;
    size_bytes: number;
}

interface OwnProps {
    disabled: boolean;
    formikProps: FormikProps<GeneralInformationFormData>;

    photosUrl: string;
    existingFiles: FilesListItem[];
    deleteFile: (fileId: number, formikActions: FormikActions<GeneralInformationFormData>) => Promise<void>;
}

interface OwnState {
    deletingFileId: number | null;
    deletingFileError: string | null;
}

export class MediaCard extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.renderVideoRow = this.renderVideoRow.bind(this);
        this.handleDeleteFile = this.handleDeleteFile.bind(this);
        this.renderWeddingPacketFile = this.renderWeddingPacketFile.bind(this);

        this.state = {
            deletingFileError: null,
            deletingFileId: null,
        };
    }

    render() {
        const {
            formikProps: { values, errors },
            existingFiles,
        } = this.props;

        return (
            <div className='VenueManagement--card VenueManagement--media'>
                {this.props.disabled && (
                    <div className='VenueManagement--card-overlay'>
                        <div className='VenueManagement--card-overlay--content'>You must save the venue first before uploading media.</div>
                    </div>
                )}
                <div className='VenueManagement--card--header'>Media</div>
                <div className='VenueManagement--card--body'>
                    <div className='VenueManagement--row'>
                        <div className='VenueManagement--col'>
                            <div className='VenueManagement--card--section'>
                                <div className='VenueManagement--card--section--header'>Photos</div>
                                <a
                                    className='nx-button nx-button--small VenueManagement--media--photos'
                                    href={this.props.photosUrl}
                                    target='_blank'
                                >
                                    MANAGE PHOTOS
                                </a>
                            </div>
                            <div className='VenueManagement--card--section'>
                                <div className='VenueManagement--card--section--header'>Videos</div>
                                <div>
                                    <FieldArray
                                        name='videos'
                                        render={(arrayHelpers) => {
                                            return (
                                                <div>
                                                    {values.videos.map((item, idx) => this.renderVideoRow(item, idx, arrayHelpers))}
                                                    <button
                                                        className={`VenueManagement--brand-link
                                                                 VenueManagement--brand-link__video`}
                                                        type='button'
                                                        onClick={() => {
                                                            // Add another video to the end of the list
                                                            arrayHelpers.push({
                                                                url: '',
                                                                order: values.videos.length + 1,
                                                            });
                                                        }}
                                                    >
                                                        + Add {values.videos.length > 0 ? 'Another' : 'a'} video
                                                    </button>
                                                </div>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='VenueManagement--col-divider' />
                        <div className='VenueManagement--col'>
                            <div className='VenueManagement--card--section'>
                                <div className='VenueManagement--card--section--header'>Wedding Packet</div>
                                {!!errors.wedding_packet_files_to_upload && (
                                    <div className='VenueManagement--form-level-error'>{errors.wedding_packet_files_to_upload}</div>
                                )}
                                <Field label='Upload Packet(s)' component={InputFile} name='wedding_packet_files_to_upload' multi={true} />
                                {existingFiles.length > 0 && (
                                    <div className='VenueManagement--wedding-packets'>
                                        <div className='VenueManagement--wedding-packets--header'>Uploaded files:</div>
                                        {!!this.state.deletingFileError && (
                                            <div className='VenueManagement--form-level-error'>{this.state.deletingFileError}</div>
                                        )}
                                        {!!existingFiles && existingFiles.length && (
                                            <div className='VenueManagement--wedding-packets--list'>
                                                {existingFiles.map(this.renderWeddingPacketFile)}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <div className='VenueManagement--media--subject'>
                                        <Field label='Subject' component={TextInput} name='wedding_packet_email_subject' />
                                    </div>
                                    <div className='VenueManagement--textarea-container'>
                                        <Field label='Message' component={TextArea} name='wedding_packet_email_body' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderVideoRow(item: VideoData, idx: number, arrayHelpers: FieldArrayRenderProps) {
        const {
            formikProps: { values, setFieldValue },
        } = this.props;

        let orderChoices: [number, string][] = [];
        for (let i = 0; i < values.videos.length; i++) {
            orderChoices.push([i + 1, `${i + 1}`]);
        }

        return (
            <div className='VenueManagement--row VenueManagement--media--videoitem' key={idx}>
                <div className='VenueManagement--col'>
                    <Field label='Youtube or Vimeo' component={TextInput} name={`videos.${idx}.url`} value={item.url || ''} />
                </div>
                <div className='VenueManagement--col-divider-sm' />
                <div className='VenueManagement--col VenueManagement--media--order'>
                    <Field
                        label='Order'
                        component={MaterialSelectInput}
                        name={`videos.${idx}.order`}
                        value={item.order}
                        choices={orderChoices}
                        customOnChange={(oldval: number, newval: number) => {
                            // All videos should have an order as they're added to the
                            // list. If two videos have the same order, simply swap
                            // the order of the current video being changes with the
                            // video that previously had that order. i.e.
                            //
                            // [1, 2, 3] if idx 0 is set to 3, find which idx had 3 (idx 2)
                            // and swap -> [3, 2, 1]
                            let currentOrders = values.videos.map((item, _) => item.order);

                            let searchVal = newval;

                            for (let i = 0; i < currentOrders.length; i++) {
                                if (i === idx) {
                                    continue;
                                }

                                let curOrder = currentOrders[i];
                                if (curOrder === searchVal) {
                                    setFieldValue(`videos.${i}.order`, oldval);
                                    break;
                                }
                            }
                        }}
                    />
                </div>
                <div className='VenueManagement--col-divider-sm' />
                <div className='VenueManagement--col VenueManagement--media--delete'>
                    <span
                        className='icon-hb-nx-trash VenueManagement--media--delete-icon'
                        onClick={() => {
                            // Renumber videos
                            type IndexedVid = {
                                vid: VideoData;
                                idx: number;
                            };
                            arrayHelpers.form.values.videos
                                .map((vid: VideoData, vidx: number) => {
                                    return {
                                        vid: vid,
                                        idx: vidx,
                                    };
                                })
                                .filter((i: IndexedVid) => i.idx !== idx)
                                .sort((f: IndexedVid, s: IndexedVid) => {
                                    const forder = f.vid.order;
                                    const sorder = s.vid.order;
                                    if (forder === null && sorder === null) {
                                        return 0;
                                    }
                                    if (sorder === null) {
                                        return -1;
                                    }
                                    if (forder === null) {
                                        return 1;
                                    }

                                    return forder < sorder ? -1 : forder > sorder ? 1 : 0;
                                })
                                .map((v: IndexedVid, vidx: number) => {
                                    setFieldValue(`videos.${v.idx}.order`, vidx + 1);
                                });

                            arrayHelpers.remove(idx);
                        }}
                    />
                </div>
            </div>
        );
    }

    private renderWeddingPacketFile(item: WeddingPacketFileInfo, idx: number) {
        return (
            <SpinnerOverlay
                key={idx}
                isSpinning={this.state.deletingFileId === item.file_id}
                spinnerProps={{
                    spinnerColor: 'black',
                    spinnerDiameter: 25,
                }}
            >
                <div className='VenueManagement--wedding-packets--item'>
                    <span className='VenueManagement--wedding-packets--text'>
                        <a href={item.url} target='_blank' className='VenueManagement--wedding-packets--link'>
                            {item.name}
                        </a>
                    </span>
                    <span className='VenueManagement--wedding-packets--size'>{`(${filesize(item.size_bytes)})`}</span>
                    <span
                        className='icon-hb-nx-trash VenueManagement--wedding-packets--icon'
                        onClick={() => this.handleDeleteFile(item.file_id)}
                    />
                </div>
            </SpinnerOverlay>
        );
    }

    private setDeletingFile(id: number) {
        this.setState({
            deletingFileError: null,
            deletingFileId: id,
        });
    }

    private setDoneDeletingFile() {
        this.setState({ deletingFileId: null });
    }

    private handleDeleteFile(id: number) {
        this.setDeletingFile(id);

        this.props
            .deleteFile(id, this.props.formikProps)
            .then(() => {
                // Remove file from list
            })
            .catch((err: WsFormikError<{}>) => {
                if (!!err.__errors__) {
                    this.setState({ deletingFileError: err.__errors__ });
                } else {
                    this.setState({
                        deletingFileError: 'An unknown error occurred, please try again later',
                    });
                }
            })
            .then(() => {
                this.setDoneDeletingFile();
            });
    }
}
